import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { IconCalendarDays } from './landing/icons';

/** Local midnight from an ISO date. `new Date('2026-03-25')` is UTC and lands a day early west of us. */
function parseIso(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) {
    return null;
  }

  const [, y, m, d] = match.map(Number);
  const date = new Date(y, m - 1, d);
  // Rejects 31 February, which Date would silently roll into March.
  return date.getMonth() === m - 1 && date.getDate() === d ? date : null;
}

function toIso(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/** dd.mm.yyyy — how a date is written in Bulgaria, and unambiguous in English too. */
function format(date: Date): string {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${day}.${month}.${date.getFullYear()}`;
}

/**
 * Accepts what people actually type: 5.3.26, 05/03/2026, 5 3 2026, 20260305.
 * Day first throughout, because that is the convention here and mixing the two silently would be
 * worse than refusing.
 */
function parseTyped(text: string): Date | null {
  const digits = text.replace(/\D/g, '');

  if (digits.length === 8) {
    // Either ddmmyyyy or yyyymmdd; a leading year is the only reading where the first four digits
    // are a plausible year and the next two a plausible month.
    const asYearFirst = parseIso(`${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6, 8)}`);
    if (asYearFirst && Number(digits.slice(0, 4)) > 1900) {
      return asYearFirst;
    }
  }

  const parts = text.split(/[^\d]+/).filter(Boolean).map(Number);

  if (parts.length < 3) {
    return null;
  }

  const [day, month, rawYear] = parts;
  const year = rawYear < 100 ? 2000 + rawYear : rawYear;

  return parseIso(`${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`);
}

/**
 * A date field that belongs to this product rather than to the browser.
 *
 * `<input type="date">` renders the OS control in the *browser's* locale, so a Bulgarian page was
 * showing `mm/dd/yyyy` and an operating-system calendar — the last unstyled control in the app, and
 * the one most likely to be misread, because 03/01/2026 means two different days depending on who
 * is looking.
 *
 * The text input stays authoritative: typing is faster than clicking for anyone entering a run of
 * invoices, and the popover is there for the days you have to count to.
 */
export function DateField({
  value,
  onChange,
  required,
  id,
}: {
  /** ISO date, or empty. */
  value: string;
  onChange: (iso: string) => void;
  required?: boolean;
  id?: string;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage === 'en' ? 'en-GB' : 'bg-BG';

  const selected = useMemo(() => parseIso(value), [value]);

  const [text, setText] = useState(() => (selected ? format(selected) : ''));
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(() => selected ?? new Date());

  const root = useRef<HTMLDivElement>(null);

  // Follows the value when something else changes it — picking a saved client rewrites the dates.
  useEffect(() => {
    setText(selected ? format(selected) : '');
    if (selected) {
      setCursor(selected);
    }
  }, [selected]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const close = (event: MouseEvent) => {
      if (!root.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  /* Short, not narrow: the Bulgarian narrow forms are "п в с ч п с н", where Monday and Friday are
     both "п" and Wednesday and Saturday are both "с" — a header row you cannot read. */
  const weekdays = useMemo(() => {
    const fmt = new Intl.DateTimeFormat(locale, { weekday: 'short' });
    // 5 January 2026 is a Monday, which is where the week starts here.
    return Array.from({ length: 7 }, (_, i) => fmt.format(new Date(2026, 0, 5 + i)));
  }, [locale]);

  const monthLabel = useMemo(
    () => new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(cursor),
    [cursor, locale],
  );

  const cells = useMemo(() => {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const offset = (first.getDay() + 6) % 7;
    return Array.from(
      { length: 42 },
      (_, i) => new Date(cursor.getFullYear(), cursor.getMonth(), 1 - offset + i),
    );
  }, [cursor]);

  /** Commits on blur rather than on every keystroke, so "5.3" is not read as an incomplete date. */
  function commit() {
    if (!text.trim()) {
      onChange('');
      return;
    }

    const parsed = parseTyped(text);

    if (parsed) {
      /* Normalise here rather than leaving it to the effect. Typing "05/03/2027" when the value is
         already 2027-03-05 changes nothing upstream, so the effect never runs and the field keeps
         the slashes — the one input in the app that did not settle into a single format. */
      setText(format(parsed));
      onChange(toIso(parsed));
    } else {
      // Unparseable input snaps back rather than silently clearing a date that was already right.
      setText(selected ? format(selected) : '');
    }
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Enter') {
      event.preventDefault();
      commit();
      setOpen(false);
    }

    if (event.key === 'Escape') {
      setOpen(false);
    }

    if (event.key === 'ArrowDown' && !open) {
      event.preventDefault();
      setOpen(true);
    }
  }

  const today = new Date();
  const todayIso = toIso(today);

  return (
    <div className="datefield" ref={root}>
      <input
        id={id}
        className="field__input datefield__input"
        inputMode="numeric"
        autoComplete="off"
        placeholder={t('dateField.placeholder')}
        required={required}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        onKeyDown={onKeyDown}
      />

      <button
        type="button"
        className="datefield__toggle"
        aria-label={t('dateField.open')}
        aria-expanded={open}
        onClick={() => setOpen((was) => !was)}
      >
        <IconCalendarDays size={16} />
      </button>

      {open && (
        <div className="datefield__pop" role="dialog" aria-label={monthLabel}>
          <div className="datefield__head">
            <button
              type="button"
              className="datefield__nav"
              aria-label={t('calendar.prevMonth')}
              onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}
            >
              ‹
            </button>
            <span className="datefield__month">{monthLabel}</span>
            <button
              type="button"
              className="datefield__nav"
              aria-label={t('calendar.nextMonth')}
              onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}
            >
              ›
            </button>
          </div>

          <div className="datefield__weekdays" aria-hidden="true">
            {weekdays.map((day, i) => (
              <span key={`${day}-${i}`}>{day}</span>
            ))}
          </div>

          <div className="datefield__grid">
            {cells.map((date) => {
              const iso = toIso(date);
              const classes = [
                'datefield__day',
                date.getMonth() !== cursor.getMonth() && 'is-outside',
                iso === todayIso && 'is-today',
                iso === value && 'is-selected',
              ]
                .filter(Boolean)
                .join(' ');

              return (
                <button
                  key={iso}
                  type="button"
                  className={classes}
                  onClick={() => {
                    onChange(iso);
                    setOpen(false);
                  }}
                >
                  {date.getDate()}
                </button>
              );
            })}
          </div>

          <div className="datefield__foot">
            <button
              type="button"
              className="btn btn--small"
              onClick={() => {
                onChange(todayIso);
                setOpen(false);
              }}
            >
              {t('calendar.today')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
